import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import type { TemplateOnboardingConfig } from "./template-onboarding.types";

const logger = new Logger("templateOnboarding");

// Internal writable atom that holds the cached config (or null).
// null means "no onboarding" (row doesn't exist or was completed).
// undefined means "not yet loaded".
const $templateOnboardingValue = atomFamily((_documentId: string) => {
	const valueAtom = atom(undefined as TemplateOnboardingConfig | null | undefined);
	valueAtom.debugPrivate = true;
	return valueAtom;
});

export const $templateOnboarding = atomFamily((documentId: string) => {
	const readAtom = atom(async (get): Promise<TemplateOnboardingConfig | null> => {
		const cached = get($templateOnboardingValue(documentId));
		if (cached !== undefined) {
			return cached;
		}

		const cache = getCache();
		const config = await cache.templateOnboarding.get(documentId);
		return config;
	});
	readAtom.debugLabel = `templateOnboarding/${documentId}`;
	return readAtom;
});

/** Write-only atom: inserts a template onboarding config for a document. */
export const $setTemplateOnboarding = atom(
	null,
	async (_get, set, params: { documentId: string; config: TemplateOnboardingConfig }) => {
		const { documentId, config } = params;
		const cache = getCache();
		await cache.templateOnboarding.insert(documentId, config);
		set($templateOnboardingValue(documentId), config);
		logger.debug("Template onboarding config saved", { documentId });
	}
);
$setTemplateOnboarding.debugLabel = "setTemplateOnboarding";

/** Write-only atom: completes (dismisses) template onboarding by deleting the cache row. */
export const $completeTemplateOnboarding = atom(null, async (_get, set, documentId: string) => {
	const cache = getCache();
	await cache.templateOnboarding.delete(documentId);
	set($templateOnboardingValue(documentId), null);
	logger.debug("Template onboarding completed", { documentId });
});
$completeTemplateOnboarding.debugLabel = "completeTemplateOnboarding";
